import React from 'react';
import { Tag as Tg, Avatar } from 'antd';
import { CloseLarge, Close } from '@nackle/origami-icons';

/**
 * A tag component
 *
 * {@link https://ant.design/components/tag}
 *
 * @module Tag
 * @param {Object} props - the component props
 * @param {string} [props.size] - the size of the tag
 * @param {boolean} [props.closable] - whether or not the tag can be closed
 * @returns {React.ReactElement}
**/
const Tag = ( { children, ...props } ) => {
    const { size, closable } = props;
    switch ( size ) {
        case 'mini':
            return <Tg { ...props } closeIcon={ closable ? <CloseLarge width='8' height='8' fill='#59606D' /> : null } className={ closable ? `generic-tag-${ size } generic-close-tag-${ size }` : `generic-tag-${ size }` }> <div className="tag-content">{ children }</div> </Tg>;
        case 'small':
            return <Tg { ...props } closeIcon={ closable ? <CloseLarge width='10' height='10' fill='#59606D' /> : null } className={ closable ? `generic-tag-${ size } generic-close-tag-${ size }` : `generic-tag-${ size }` }> <div className="tag-content">{ children }</div> </Tg>;
        case 'medium':
            return <Tg { ...props } closeIcon={ closable ? <CloseLarge width='12' height='12' fill='#59606D' /> : null } className={ closable ? `generic-tag-${ size } generic-close-tag-${ size }` : `generic-tag-${ size }` }> <div className="tag-content">{ children }</div> </Tg>;
        case 'large':
            return <Tg { ...props } closeIcon={ closable ? <CloseLarge width='14' height='14' fill='#59606D' /> : null } className={ closable ? `generic-tag-${ size } generic-close-tag-${ size }` : `generic-tag-${ size }` }> <div className="tag-content">{ children }</div> </Tg>;
        default:
            return <Tg { ...props } closeIcon={ closable ? <CloseLarge width='12' height='12' fill='#59606D' /> : null } className={ closable ? `generic-tag-${ size } generic-close-tag-${ size }` : `generic-tag-${ size }` }> <div className="tag-content">{ children }</div> </Tg>;
    }
};

/**
 * A person tag component
 *
 * {@link https://ant.design/components/tag}
 *
 * @module Tag.Person
 * @param {Object} props - the component props
 * @param {string} [props.shape] - the tag shape
 * @param {string} [props.size] - the size of the tag
 * @param {boolean} [props.closable] - whether or not the tag can be closed
 * @param {string} [props.initial] - the person's initial
 * @returns {React.ReactElement}
**/
Tag.Person = ( { children, ...props } ) => {
    const { src, shape, closable, initial } = props;
    switch ( shape ) {
        case 'square':
            return <Tg { ...props }
                className={ closable ? 'square-tag-close person-tag' : 'square-tag person-tag' }
                icon={ <Avatar src={ src } className="primary-background" shape='square'></Avatar> }
                closeIcon={ closable ? <Close width='16' height='16' fill='#59606D' /> : null }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'round':
            return <Tg { ...props }
                className={ closable ? 'round-tag-close person-tag' : 'round-tag person-tag' }
                icon={ <Avatar src={ src } className="primary-background" shape='circle' >{ initial }</Avatar> }
                closeIcon={ closable ? <Close width='16' height='16' fill='#59606D' /> : null }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        default:
            return <Tg { ...props }
                className={ closable ? 'square-tag-close person-tag' : 'square-tag person-tag' }
                icon={ <Avatar src={ src } className="primary-background" shape='square' >{ initial }</Avatar> }
                closeIcon={ closable ? <Close width='16' height='16' fill='#59606D' /> : null }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
    }
};

/**
 * A color tag component
 *
 * {@link https://ant.design/components/tag}
 *
 * @module Tag.Color
 * @param {Object} props - the component props
 * @param {string} [props.size] - the size of the tag
 * @param {string} [props.status] - the status of the tag
 * @returns {React.ReactElement}
**/
Tag.Color = ( { children, ...props } ) => {
    const { size, status } = props;
    switch ( status ) {
        case 'error':
            return <Tg { ...props }
                color='#DE1B1B'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'warning':
            return <Tg { ...props }
                color='#FCC419'
                className={ `generic-tag-${ size } warning-tag color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'success':
            return <Tg { ...props }
                color='#038537'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'processing':
            return <Tg { ...props }
                color='#1D5BBF'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'purple':
            return <Tg { ...props }
                color='#7D3CBD'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'default':
            return <Tg { ...props }
                color='#59606D'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        default:
            return <Tg { ...props }
                color='#59606D'
                className={ `generic-tag-${ size } color-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;

    }
};

/**
 * A color tag component
 *
 * {@link https://ant.design/components/tag}
 *
 * @module Tag.Color
 * @param {Object} props - the component props
 * @param {string} [props.size] - the size of the tag
 * @param {string} [props.status] - the status of the tag
 * @returns {React.ReactElement}
**/
Tag.Lightcolor = ( { children, ...props } ) => {
    const { size, status } = props;
    switch ( status ) {
        case 'error':
            return <Tg { ...props }
                color='#FAF0F0'
                className={ `${ status } generic-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'warning':
            return status === 'warning' &&
                <Tg { ...props }
                    color='#FFF9DB'
                    className={ `${ status } generic-tag-${ size }` }
                >
                    <div className="tag-content">{ children }</div>
                </Tg>;
        case 'success':
            return <Tg { ...props }
                color='#E1FAEB'
                className={ `${ status } generic-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'processing':
            return <Tg { ...props }
                color='#F0F5FC'
                className={ `${ status } generic-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        case 'purple':
            return <Tg { ...props }
                color='#F5F0FA'
                className={ `${ status } generic-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
        default:
            return <Tg { ...props }
                color='#FAF0F0'
                className={ `${ status } generic-tag-${ size }` }
            >
                <div className="tag-content">{ children }</div>
            </Tg>;
    }
};

export { Tag };
